import { PieSeriesOption } from 'echarts/charts';
import { BarSeriesOption, CallbackDataParams, DatasetOption } from 'echarts/types/dist/shared';

import { I18nService } from '@core/i18n-legacy';
import { DatasetBase } from '@core/reports-types';
import { datasetValueFormatterOptions } from '@core/reports-util';
import { SupportedNumberFormat, valueFormatter } from '@core/utils-legacy';

import { BAR_CHART_BAR_MAX_WIDTH } from '../chart-layout';

export const addBreakdownPieSeries =
  (dataset: DatasetBase, i18n: I18nService) =>
  <T extends { dataset: DatasetOption }>(opts: T): T & { series: [PieSeriesOption] } => ({
    ...opts,
    series: [
      {
        type: <const>'pie',
        label: {
          show: dataset.isSingleDimension,
          position: <const>'outside',
          formatter: (params: CallbackDataParams) => {
            const name = dataset.getCategoryNameById(params.name);
            const data = Array.isArray(params.data) ? params.data : [params.data];
            const value = <string | number>data[params.encode?.value[0]!];
            const valueFormatted = valueFormatter(
              value,
              datasetValueFormatterOptions(dataset),
              i18n,
              {
                number: { format: SupportedNumberFormat.IntlCompact },
              },
            );
            return `${name}\n{pct|${valueFormatted}}`;
          },
        },
      },
    ],
  });

const barChartLabel = (dataset: DatasetBase, i18n: I18nService) => ({
  silent: true,
  padding: 0,
  margin: 0,
  show: dataset.isSingleDimension,
  position: <const>'right',
  formatter: (params: any) => {
    const data = Array.isArray(params.data) ? params.data : [params.data];
    const value = <string | number>data[params.encode?.x[0]!];
    return valueFormatter(value, datasetValueFormatterOptions(dataset), i18n, {
      number: { format: SupportedNumberFormat.IntlCompact },
    });
  },
});

/**
 *
 * @param stack - primary or secondary group
 * @param dataset
 * @param i18n
 */
export const addBreakdownBarSeries =
  (dataset: DatasetBase, i18n: I18nService, stack: string | undefined) =>
  <T extends { dataset: DatasetOption }>(
    opts: T,
  ): T & {
    series: BarSeriesOption[];
  } => ({
    ...opts,
    series: dataset.dimensions.slice(1).map(dimension => ({
      id: dimension.id || dimension.name,
      name: dimension.name,
      type: 'bar',
      seriesLayoutBy: 'column',
      stack,
      emphasis: {
        focus: 'series',
      },
      label: barChartLabel(dataset, i18n),
      barMaxWidth: BAR_CHART_BAR_MAX_WIDTH,
    })),
  });
